import { SettlementFragmentOverview } from './settlements'

export const ViewDataFragment = gql`
  fragment ViewDataFragment on ViewData {
    totalCount
    rows {
      ...ViewDataRowFragment
    }
  }
  fragment ViewDataRowFragment on ViewDataRow {
    id
    columns {
      ...ViewDataElementFragment
    }
    organization {
      id
      paymentProviderFormatted
      timeZone
    }
    entity {
      ...EntityForViewFragment
    }

    # entities specific to each view type
    record {
      ...RecordFragmentOverview
    }
    violation {
      ...ViolationFragmentOverview
    }
    recordTaskInspectionAttempt {
      ...RecordTaskInspectionAttemptOverviewFragment
    }
    recordTask {
      ...RecordTaskFragmentOverview
    }
    recordTaskFee {
      ...RecordTaskFeeFragmentOverview
    }
    recordTaskPayment {
      ...RecordTaskPaymentFragmentOverview
    }
    paymentTransfer {
      ...PaymentTransferFragmentOverview
    }
    settlement {
      ...SettlementFragmentOverview
    }
    file {
      ...FileFragment
    }
    fileOnRecordType
    recordGeneratedDocument {
      ...RecordGeneratedDocumentFragmentOverview
    }
    recordPlansSet {
      ...RecordPlansSetFragmentOverview
    }
    meeting {
      ...MeetingFragmentOverview
    }
  }
  fragment ViolationFragmentOverview on Violation {
    id
    identifier
    status
    uuid
    address
    violationMailers {
      id
    }
    backwardsCompatibleLocationInformation {
      ...BackwardsCompatibleLocationInformationFragment
    }
  }
  fragment RecordTaskFragmentOverview on RecordTask {
    id
    name
    type
    isAutomatic
    automaticExplanation
    recordTaskGroup {
      id
      record {
        id
        uuid
        identifier
        issuedIdentifier
        mainBackwardsCompatibleLocationInformation {
          ...BackwardsCompatibleLocationInformationFragment
        }
      }
      workflow {
        id
      }
    }
    ownerUser {
      ...UserDataFragment
    }
    dueDate
  }
  fragment RecordTaskFeeFragmentOverview on RecordTaskFee {
    id
  }
  fragment RecordTaskPaymentFragmentOverview on RecordTaskPayment {
    id
    dueDate
    isPayable
    totalWhenInvoiceRequestedCents
  }
  fragment PaymentTransferFragmentOverview on PaymentTransfer {
    id
  }
  fragment RecordGeneratedDocumentFragmentOverview on RecordGeneratedDocument {
    id
    documentTemplate {
      id
      name
    }
  }
  fragment RecordPlansSetFragmentOverview on RecordPlansSet {
    id
    createdAt
    versionDisplayName
    versionNumber
  }
  fragment MeetingFragmentOverview on Meeting {
    id
    uuid
    startTime
    location
    meetingBoard {
      id
      name
    }
    recordsOnMeeting {
      id
    }
  }
  ${SettlementFragmentOverview}
`
export const ViewDataElementFragment = gql`
  fragment ViewDataElementFragment on ViewDataElement {
    ... on ViewDataElementBoolean {
      bool
    }
    ... on ViewDataElementDate {
      date
    }
    ... on ViewDataElementDateTime {
      dateTime
    }
    ... on ViewDataElementRecord {
      record {
        id
        uuid
        identifier
        issuedIdentifier
        isDraft
        isTest
        currentUserHasAccess
        organization {
          slug
        }
      }
    }
    ... on ViewDataElementRecordActionRequiredBy {
      recordActionRequiredBy
    }
    ... on ViewDataElementRecordTaskGroupType {
      recordTaskGroupType
    }
    ... on ViewDataElementRecordTemplate {
      recordTemplate {
        id
        name
      }
    }
    ... on ViewDataElementRecordTypeStatus {
      recordTypeStatus {
        id
        name
        type
      }
    }
    ... on ViewDataElementString {
      string
    }
    ... on ViewDataElementUser {
      user {
        ...UserDataFragment
      }
    }
    ... on ViewDataElementViolation {
      violation {
        id
        identifier
      }
    }
    ... on ViewDataElementViolationStatus {
      violationStatus
    }
    ... on ViewDataElementViolationType {
      violationType {
        id
        name
      }
    }
    ... on ViewDataElementInspectionTemplate {
      inspectionTemplate {
        id
        name
      }
    }
    ... on ViewDataElementRecordTaskInspectionStatus {
      recordTaskInspectionStatus
    }
    ... on ViewDataElementRecordTaskInspectionResult {
      recordTaskInspectionResult {
        id
        name
        isPassing
      }
    }
    ... on ViewDataElementRecordTaskStatus {
      recordTaskStatus
    }
    ... on ViewDataElementRecordTaskType {
      recordTaskType
    }
    ... on ViewDataElementFeeType {
      fee {
        id
        name
      }
    }
    ... on ViewDataElementFieldType {
      field {
        ...FieldFragment
      }
    }
    ... on ViewDataElementGeneralLedgerNumberType {
      generalLedgerNumber {
        id
        glNumber
      }
    }
    ... on ViewDataElementRecordTypeType {
      recordType {
        id
        displayName
      }
    }
    ... on ViewDataElementRecordTaskPaymentStatusType {
      recordTaskPaymentStatus
    }
    ... on ViewDataElementPaymentMethodType {
      paymentMethod
    }
    ... on ViewDataElementPaymentTransferType {
      paymentTransferType
      displayLabel
    }
    ... on ViewDataElementOrganizationFinixMerchantType {
      organizationFinixMerchant {
        id
        description
      }
    }
    ... on ViewDataElementOrganization {
      organization {
        displayName
      }
    }
    ... on ViewDataElementRecordPlansSet {
      recordPlansSet {
        id
        versionDisplayName
      }
    }
    ... on ViewDataElementRecordTypeApplicationGroup {
      recordTypeApplicationGroup {
        id
        name
      }
    }
    ... on ViewDataElementNumber {
      number
    }
    ... on ViewDataElementCurrencyCents {
      cents
    }
    ... on ViewDataElementDuration {
      seconds
    }
    ... on ViewDataElementCompany {
      company {
        name
      }
    }
    ... on ViewDataElementFile {
      file {
        id
        name
        url {
          signedUrl
        }
      }
    }
    ... on ViewDataElementFileOnRecordType {
      fileOnRecordType
    }
    ... on ViewDataElementEntity {
      entity {
        ...EntityForViewFragment
      }
      displayName
    }
    ... on ViewDataElementMoment {
      moment {
        ...CalendarEventMomentFragment
      }
    }
    ... on ViewDataElementFeeSchedule {
      feeSchedule {
        id
        name
      }
    }
    ... on ViewDataElementSurveyResponseRating {
      surveyResponseRating
    }
    ... on ViewDataElementSurveyType {
      surveyType
    }
  }
`

export const ViewFragment = gql`
  fragment ViewFragment on View {
    id

    displayIndex
    name
    updatedAt
    viewType

    columns {
      ...ViewColumnFragment
    }
    customizableColumnTemplates {
      columnType
      dataType
      labelSentenceCase
      labelTitleCase
    }
    filters {
      ...ViewFilterFragment
    }
    recordType {
      id
    }
    sorts {
      ...ViewSortFragment
    }
  }
`

export const ViewColumnFragment = gql`
  fragment ViewColumnFragment on ViewColumn {
    columnTemplateGeneratedId
  }
`

export const ViewFilterFragment = gql`
  fragment ViewFilterFragment on ViewFilter {
    bool
    columnTemplateGeneratedId
    date {
      ...ViewFilterDateFragment
    }
    isNull
    number {
      ...ViewFilterNumberFragment
    }
    membership {
      ...ViewFilterMembershipFragment
    }
  }
`

export const ViewFilterMembershipFragment = gql`
  fragment ViewFilterMembershipFragment on ViewFilterMembership {
    type
    optionsSelected
  }
`

export const ViewFilterNumberFragment = gql`
  fragment ViewFilterNumberFragment on ViewFilterNumber {
    comparison
    number
  }
`

export const ViewFilterDateFragment = gql`
  fragment ViewFilterDateFragment on ViewFilterDate {
    exactEnd
    exactStart
    relativeToTodayCount
    relativeToTodayDelayType
    relativeToTodayDirection
    type
  }
`

export const ViewSortFragment = gql`
  fragment ViewSortFragment on ViewSort {
    columnTemplateGeneratedId
    mode
  }
`

export const ViewColumnTemplateFragment = gql`
  fragment ViewColumnTemplateFragment on ViewColumnTemplate {
    columnType
    dataType
    generatedId
    labelSentenceCase
    labelTitleCase
    fieldTemplates {
      ...FieldTemplateForViewFragment
    }
    filterOptions {
      ...ViewFilterOptionFragment
    }
    includeByDefault
    isFilterable
    isSortable
    isNullable
    usedInViews {
      ...ViewColumnUsedInViewFragment
    }
  }
`

export const ViewColumnUsedInViewFragment = gql`
  fragment ViewColumnUsedInViewFragment on View {
    id
    name
  }
`

export const ViewFilterOptionFragment = gql`
  fragment ViewFilterOptionFragment on ViewFilterOption {
    identifier
    isArchived

    data {
      ...ViewDataElementFragment
    }
  }
`

export const FieldTemplateForViewFragment = gql`
  fragment FieldTemplateForViewFragment on Field {
    id
    isArchived
    label
    requireReviewOnRenewal
    type
    fieldGroupTemplate {
      id
      isSharedFieldGroup
      usedInRecordTemplates {
        id
        name
      }
    }
  }
`

export const CandidateCustomColumnFragment = gql`
  fragment CandidateCustomColumnFragment on CandidateCustomColumn {
    columnType
    sourceId
    label
    sectionLabel
  }
`

export const EntityForViewFragment = gql`
  fragment EntityForViewFragment on Entity {
    id
    name
    uuid
    activeConditionCount
    layer {
      type
    }
    addressDisplayName
  }
`
